// TODO: reenvio de SMS desabilitado temporariamente (em avaliacao)
// import { AuthorizationStatusEnum } from "@/app/services/authorizationService/getAuthorization";
import { addMoneySimbols } from "@/app/utils/formatMoneyValue";
import { cn } from "@/app/utils/cn";
import masks from "@/app/utils/masks";
import ButtonBack from "@/view/components/ButtonBack";
import AuthorizationStatus from "@/view/components/CrefazClt/AuthorizationStatus";
import { Button } from "@/view/components/ui/button";
import { Input } from "@/view/components/ui/input";
import { Slider } from "@/view/components/ui/slider";
import { Dialog } from "@/view/components/ui/dialog";
import { Spinner } from "@/view/components/ui/spinner";
import NewCrefazCltModal from "./components/NewCrefazCltModal";
import useCrefazCltSimulation from "./useCrefazCltSimulation";

const CustomerLine = ({
  customer,
}: {
  customer?: { name: string; cpf: string };
}) => {
  if (!customer?.name) return null;

  return (
    <p className="-mt-2 text-sm text-black04">
      <strong className="font-semibold text-black01">{customer.name}</strong>
      {customer.cpf ? ` · ${masks.cpf(customer.cpf)}` : ""}
    </p>
  );
};

const MAJOR_TERMS = [48, 36, 24, 12, 6];

const SliderTicks = ({ terms }: { terms: number[] }) => {
  const lastPosition = Math.max(terms.length - 1, 1);

  return (
    <div className="relative mt-1 h-3">
      {terms.map((term, position) => (
        <span
          key={term}
          style={{ left: `${(position / lastPosition) * 100}%` }}
          className={cn(
            "absolute top-0 w-px -translate-x-1/2 bg-gray-300",
            MAJOR_TERMS.includes(term) ? "h-3" : "h-1.5",
          )}
        />
      ))}
    </div>
  );
};

const formatDate = (value: string) => {
  if (!value) return "";

  const [year, month, day] = value.slice(0, 10).split("-");

  return `${day}/${month}/${year}`;
};

export default function CrefazCltSimulation() {
  const {
    authorization,
    isAuthorized,
    simulation,
    loading,
    isRecalculating,
    error,
    amountInput,
    amountError,
    handleAmountChange,
    isConfirming,
    handleConfirmOffer,
    isProposalModalOpen,
    setIsProposalModalOpen,
    offers,
    sortedOffers,
    sliderPosition,
    handleSliderChange,
    selectedOffer,
    valorSolicitado,
    commission,
  } = useCrefazCltSimulation();

  if (authorization.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="flex flex-col gap-6">
        <ButtonBack label="Crefaz CLT" />

        <CustomerLine customer={authorization.customer} />

        <div className="w-full max-w-xl rounded-2xl border bg-white p-5">
          <AuthorizationStatus
            status={authorization.status}
            observation={authorization.observation}
            smsSent={authorization.smsSent}
          />

          {/* TODO: reenvio de SMS desabilitado temporariamente (em avaliacao)
          {authorization.status === AuthorizationStatusEnum.PENDING &&
            authorization.smsSent && (
              <Button
                className="mt-6 w-[200px] rounded-full font-semibold text-white"
                onClick={authorization.sendSms}
                isLoading={authorization.isSendingSms}
                disabled={authorization.isSendingSms}
              >
                Reenviar SMS
              </Button>
            )} */}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <ButtonBack label="Crefaz CLT" />

        <div className="flex h-[50vh] flex-col items-center justify-center gap-1">
          <span className="font-semibold text-black01">
            Não foi possível simular
          </span>
          <span className="text-black03">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <ButtonBack label="Crefaz CLT" />

      <div className="w-full max-w-md rounded-2xl border bg-white p-5">
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium">
            Valor solicitado
          </label>

          <Input
            value={amountInput}
            maxLength={12}
            onChange={(event) => handleAmountChange(event.target.value)}
          />

          {amountError ? (
            <p className="mt-1 text-sm text-error">{amountError}</p>
          ) : (
            simulation && (
              <p className="mt-1 text-sm text-black04">
                Até{" "}
                {addMoneySimbols(simulation.valorLimite.valorMaximoSolicitado)}{" "}
                · parcela de{" "}
                {addMoneySimbols(simulation.valorLimite.valorMinimoParcela)} a{" "}
                {addMoneySimbols(simulation.valorLimite.valorMaximoParcela)}
              </p>
            )
          )}
        </div>

        {simulation && (
          <div className="mb-4 space-y-2 rounded-xl bg-gray-50 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-black04">Valor solicitado</span>
              <span className="font-semibold">
                {addMoneySimbols(valorSolicitado)}
              </span>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-black04">Comissão ({commission}%)</span>
              <span className="font-semibold">
                {addMoneySimbols((valorSolicitado * commission) / 100)}
              </span>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-black04">Primeiro vencimento</span>
              <span className="font-semibold">
                {formatDate(simulation.vencimento)}
              </span>
            </div>
          </div>
        )}

        <Button
          className="w-full rounded-full font-semibold text-white"
          onClick={handleConfirmOffer}
          isLoading={isConfirming}
          disabled={isConfirming || isRecalculating || !offers.length}
        >
          Selecionar oferta
        </Button>

        <Dialog
          open={isProposalModalOpen}
          onOpenChange={setIsProposalModalOpen}
        >
          <NewCrefazCltModal />
        </Dialog>
      </div>

      {isRecalculating && (
        <div className="flex justify-center pt-4">
          <Spinner />
        </div>
      )}

      {!isRecalculating && !!selectedOffer && (
        <div className="w-full max-w-md rounded-2xl border bg-white p-5">
          <span className="text-sm font-semibold uppercase tracking-wide text-black01">
            Parcela por mês
          </span>

          <div className="mt-6 flex items-baseline justify-center gap-2">
            <span className="text-4xl font-bold text-black01">
              {selectedOffer.prazo}x
            </span>
            <span className="text-xl font-bold text-primary">
              de {addMoneySimbols(selectedOffer.valorParcela)}
            </span>
          </div>

          {sortedOffers.length > 1 && (
            <div className="mt-6">
              <Slider
                min={0}
                max={sortedOffers.length - 1}
                step={1}
                value={sliderPosition}
                onChange={(event) =>
                  handleSliderChange(Number(event.target.value))
                }
              />

              <SliderTicks
                terms={sortedOffers.map((item) => item.offer.prazo)}
              />

              <div className="flex items-center justify-between text-sm font-semibold text-black04">
                <span>{sortedOffers[0].offer.prazo}x</span>
                <span>
                  {sortedOffers[sortedOffers.length - 1].offer.prazo}x
                </span>
              </div>

              <p className="mt-4 text-center text-sm text-black04">
                Arraste para escolher entre {sortedOffers[0].offer.prazo}x e{" "}
                {sortedOffers[sortedOffers.length - 1].offer.prazo}x parcelas
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
